import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import {
  getCaseQueue, getAssignableStaff, assignCase, bulkAssignCases, escalateCase,
  setCasePriority, setCaseDeadline, takeOverCase, setCaseCountry, getStaff,
  getEmergencyAccessGrants, grantEmergencyAccess, revokeEmergencyAccess, sendEmergencyTask, getCountries,
  withdrawTask, confirmTaskReturn, redistributeTask, getCaseControlSettings, updateCaseControlSetting,
  getCaseComments, addCaseComment, getDeadlineExtensions, decideDeadlineExtension,
  recordDecision, getDocumentDownload, getLegalEntities, updateLegalEntity,
  getAccessGrants, releaseAccess,
} from '../api/admin';
import { useCountry } from '../context/CountryContext';

const ASSIGNMENT_LABELS = {
  pending_acceptance: 'Awaiting acceptance', accepted: 'Accepted', declined: 'Declined',
  withdrawn: 'Withdrawn', expired: 'Expired — not accepted', escalated: 'Escalated',
  reassignment_requested: 'Return requested', access_revoked: 'Access revoked', completed: 'Done',
  submitted_for_acceptance: 'Submitted — decision needed',
};

// Case Control & Country Permissions brief (03.10.2026) — the Owner's
// unified view across damage claims, provider applications, support
// tickets, and identity verification (added as a real fourth case type —
// it lives on `users`, not a separate table, but behaves the same here).
// "incident" (the Joint Architecture PDF's "Internal review" / IR- source)
// added 07.10.2026.
const CASE_TYPE_LABELS = { damage_case: 'Damage claim', application: 'Provider application', support_ticket: 'Support ticket', verification: 'Identity verification', incident: 'Incident report' };
const SETTING_LABELS = {
  acceptance_window_urgent_minutes: 'Urgent / Emergency',
  acceptance_window_high_minutes: 'High',
  acceptance_window_normal_minutes: 'Normal',
  acceptance_window_low_minutes: 'Low',
  deadline_color_warning_minutes: 'Warning color — minutes before deadline',
  deadline_color_critical_minutes: 'Critical color — minutes before deadline',
};
// Joint Architecture PDF, section 4 — exact colors as given.
const COLOR_LABELS = { normal: 'Normal', warning: 'Warning', critical: 'Critical', escalated: 'Escalated' };
const COLOR_DOTS = { normal: '#16a34a', warning: '#eab308', critical: '#f97316', escalated: '#dc2626' };
// Joint Architecture PDF, section 2 — the four statuses, re-labeled from
// the assignment lifecycle (see routes/caseControl.js deriveCaseControlStatus).
const CASE_CONTROL_STATUS_LABELS = { open: 'Open', awaiting_acceptance: 'Awaiting acceptance', accepted: 'Accepted', submitted: 'Submitted for acceptance', done: 'Done' };
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const ACTION_OPTIONS = ['view_case', 'write_note', 'update_status', 'trigger_refund', 'block_user'];

function keyOf(c) { return `${c.case_type}:${c.case_id}`; }

const CaseControl = () => {
  // Joint Architecture: "Global country selector... applies to all lists
  // (Users, Case Control, Log, Tasks) and always shows the active value" --
  // this page no longer carries its own separate country dropdown.
  const { country: globalCountry } = useCountry();
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ case_type: '', status: '', priority: '', color: '', case_control_status: '', staff_admin_id: '', from: '', to: '' });
  const [selected, setSelected] = useState(new Set());
  const [staffRoster, setStaffRoster] = useState([]);
  const [countries, setCountries] = useState([]);
  const [commentsPanel, setCommentsPanel] = useState(null); // { caseType, caseId, displayId, comments, draft, loading }
  const [releaseModal, setReleaseModal] = useState(null); // { caseType, caseId, displayId, grants, adminId, grantsView, grantsOrder, grantsDelivery, busy, error }
  const [extensions, setExtensions] = useState([]);

  const [assignModal, setAssignModal] = useState(null); // { mode: 'single'|'bulk', caseType, caseId, candidates, loading, error }
  const [bulkStaffId, setBulkStaffId] = useState('');

  const [countryModal, setCountryModal] = useState(null); // { caseType, caseId, country_code, reason }

  const [emergencyGrants, setEmergencyGrants] = useState([]);
  const [showEmergencyForm, setShowEmergencyForm] = useState(false);
  const [emergencyForm, setEmergencyForm] = useState({ case_type: 'damage_case', case_id: '', staff_admin_id: '', allowed_actions: [], duration_hours: 24, reason: '' });

  const [settings, setSettings] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);
  const [redistributeModal, setRedistributeModal] = useState(null); // { assignmentId, staffId }
  const [decisionModal, setDecisionModal] = useState(null); // { assignmentId, report, decision, remarks }

  const load = () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (globalCountry && globalCountry !== 'ALL' && globalCountry !== 'NONE') params.country = globalCountry;
    getCaseQueue(params).then((res) => setCases(res.data?.cases || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filters, globalCountry]); // eslint-disable-line
  useEffect(() => {
    getStaff({ limit: 200 }).then((res) => setStaffRoster(res.data?.staff || []));
    getCountries().then((res) => setCountries(res.data?.countries || []));
    getEmergencyAccessGrants().then((res) => setEmergencyGrants(res.data?.grants || []));
    getCaseControlSettings().then((res) => setSettings(res.data?.settings || []));
    getDeadlineExtensions().then((res) => setExtensions(res.data?.extensions || []));
    getLegalEntities().then((res) => setLegalEntities(res.data?.entities || []));
  }, []);

  const saveLegalEntity = async (countryCode, entityName, address) => {
    await updateLegalEntity(countryCode, entityName, address);
    getLegalEntities().then((res) => setLegalEntities(res.data?.entities || []));
  };

  const openRelease = (c) => {
    setReleaseModal({ caseType: c.case_type, caseId: c.case_id, displayId: c.display_id, grants: [], adminId: '', grantsView: true, grantsOrder: false, grantsDelivery: false, busy: false, error: null, loading: true });
    getAccessGrants(c.case_type, c.case_id).then((res) => setReleaseModal((m) => (m ? { ...m, grants: res.data?.grants || [], loading: false } : m)));
  };
  const submitRelease = async () => {
    if (!releaseModal || !releaseModal.adminId || releaseModal.busy) return;
    setReleaseModal((m) => ({ ...m, busy: true, error: null }));
    try {
      await releaseAccess(releaseModal.caseType, releaseModal.caseId, {
        admin_id: releaseModal.adminId, grants_view: releaseModal.grantsView,
        grants_order_document: releaseModal.grantsOrder, grants_delivery_note: releaseModal.grantsDelivery,
      });
      openRelease({ case_type: releaseModal.caseType, case_id: releaseModal.caseId, display_id: releaseModal.displayId });
    } catch (err) {
      setReleaseModal((m) => ({ ...m, busy: false, error: err.response?.data?.error || 'Failed to release access' }));
    }
  };

  const openComments = (c) => {
    setCommentsPanel({ caseType: c.case_type, caseId: c.case_id, displayId: c.display_id, comments: [], draft: '', loading: true });
    getCaseComments(c.case_type, c.case_id).then((res) => setCommentsPanel((p) => (p ? { ...p, comments: res.data?.comments || [], loading: false } : p)));
  };
  const submitComment = async () => {
    if (!commentsPanel.draft.trim()) return;
    const res = await addCaseComment(commentsPanel.caseType, commentsPanel.caseId, commentsPanel.draft);
    setCommentsPanel((p) => ({ ...p, comments: [...p.comments, res.data.comment], draft: '' }));
  };
  const decideExtension = async (id, approve) => {
    await decideDeadlineExtension(id, approve);
    getDeadlineExtensions().then((res) => setExtensions(res.data?.extensions || []));
    load();
  };

  const saveSetting = async (key, value) => {
    await updateCaseControlSetting(key, value);
    getCaseControlSettings().then((res) => setSettings(res.data?.settings || []));
  };

  const doConfirmReturn = async (c, confirm) => { await confirmTaskReturn(c.assignment.id, confirm); load(); };

  // PDF documentation spec, step 5 — Owner records Accepted / Rework required.
  const openDecision = (c) => setDecisionModal({ assignmentId: c.assignment.id, report: c.assignment.completion_report, remarks: '' });
  const submitDecision = async (decision) => {
    try {
      await recordDecision(decisionModal.assignmentId, decision, decisionModal.remarks);
      setDecisionModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not record the decision');
    }
  };

  const downloadDocument = async (documentId) => {
    try {
      const res = await getDocumentDownload(documentId);
      window.open(res.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.error || 'Document is not ready yet.');
    }
  };
  const openRedistribute = (c) => setRedistributeModal({ assignmentId: c.assignment.id, staffId: '' });
  const submitRedistribute = async () => {
    if (!redistributeModal.staffId) return;
    try {
      await redistributeTask(redistributeModal.assignmentId, redistributeModal.staffId);
      setRedistributeModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not redistribute');
    }
  };

  const toggleSelect = (c) => {
    const k = keyOf(c);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const openAssignSingle = async (c) => {
    setAssignModal({ mode: 'single', caseType: c.case_type, caseId: c.case_id, candidates: [], loading: true, error: null, message: '', compensation_note: '', scope_note: '', acceptance_criteria_note: '' });
    try {
      const res = await getAssignableStaff(c.case_type, c.case_id);
      setAssignModal((m) => ({ ...m, candidates: res.data?.candidates || [], loading: false }));
    } catch (err) {
      setAssignModal((m) => ({ ...m, loading: false, error: err.response?.data?.error || 'Failed to load candidates' }));
    }
  };

  const confirmSingleAssign = async (adminId) => {
    try {
      await assignCase(assignModal.caseType, assignModal.caseId, adminId, assignModal.message, {
        compensation_note: assignModal.compensation_note, scope_note: assignModal.scope_note, acceptance_criteria_note: assignModal.acceptance_criteria_note,
      });
      setAssignModal(null);
      load();
    } catch (err) {
      setAssignModal((m) => ({ ...m, error: err.response?.data?.error || 'Assignment failed' }));
    }
  };

  const openBulkAssign = () => { setBulkStaffId(''); setAssignModal({ mode: 'bulk', error: null }); };

  const confirmBulkAssign = async () => {
    const selectedCases = cases.filter((c) => selected.has(keyOf(c))).map((c) => ({ case_type: c.case_type, case_id: c.case_id }));
    try {
      await bulkAssignCases(selectedCases, bulkStaffId);
      setAssignModal(null);
      setSelected(new Set());
      load();
    } catch (err) {
      const data = err.response?.data;
      const detail = data?.blocked?.map((b) => `${b.case_type} ${b.case_id.slice(0, 8)}: ${b.reason}`).join('\n');
      setAssignModal((m) => ({ ...m, error: (data?.error || 'Bulk assignment failed') + (detail ? `\n${detail}` : '') }));
    }
  };

  const doEscalate = async (c) => {
    const reason = window.prompt('Escalation reason:');
    if (!reason) return;
    await escalateCase(c.case_type, c.case_id, reason);
    load();
  };

  const doPriority = async (c, priority) => { await setCasePriority(c.case_type, c.case_id, priority); load(); };
  const doDeadline = async (c) => {
    const val = window.prompt('Deadline (YYYY-MM-DD HH:MM, blank to clear):');
    if (val === null) return;
    await setCaseDeadline(c.case_type, c.case_id, val ? new Date(val).toISOString() : null);
    load();
  };
  const doTakeOver = async (c) => { await takeOverCase(c.case_type, c.case_id); load(); };
  const doWithdraw = async (c) => {
    const needsReason = c.assignment.status === 'accepted';
    const reason = needsReason ? window.prompt('Reason for withdrawing an already-accepted task:') : null;
    if (needsReason && !reason) return;
    try {
      await withdrawTask(c.assignment.id, reason || undefined);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not withdraw');
    }
  };

  const openCountryModal = (c) => setCountryModal({ caseType: c.case_type, caseId: c.case_id, country_code: '', reason: '' });
  const saveCountry = async () => {
    if (!countryModal.country_code && countryModal.country_code !== 'ARCHIVE') return;
    await setCaseCountry(countryModal.caseType, countryModal.caseId, countryModal.country_code === 'ARCHIVE' ? null : countryModal.country_code, countryModal.reason);
    setCountryModal(null);
    load();
  };

  const submitEmergencyGrant = async (e) => {
    e.preventDefault();
    await grantEmergencyAccess(emergencyForm);
    setShowEmergencyForm(false);
    setEmergencyForm({ case_type: 'damage_case', case_id: '', staff_admin_id: '', allowed_actions: [], duration_hours: 24, reason: '' });
    getEmergencyAccessGrants().then((res) => setEmergencyGrants(res.data?.grants || []));
  };
  const doRevoke = async (id) => { await revokeEmergencyAccess(id); getEmergencyAccessGrants().then((res) => setEmergencyGrants(res.data?.grants || [])); };
  const doSendEmergencyTask = async (id) => {
    if (!window.confirm('Send this as an immediate takeover — no acceptance required from the staff member?')) return;
    try {
      await sendEmergencyTask(id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not send emergency task');
    }
  };

  const withoutCountry = cases.filter((c) => !c.country_code);

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>{t('Case Management')} › {t('Case Overview')}</div>
      <h1 style={s.h1}>{t('Case Overview')}</h1>
      <p style={s.hint}>
        Unified view across damage claims, provider applications and support tickets. Only owners
        and explicitly-approved global admins see this screen.
      </p>

      <div style={s.filters}>
        <span style={s.globalCountryTag}>Showing: {globalCountry === 'ALL' ? 'All countries' : globalCountry === 'NONE' ? 'No active country' : (countries.find((c) => c.country_code === globalCountry)?.country_name || globalCountry)} <span style={s.titleSmall}>(set in the header)</span></span>
        <select style={s.select} value={filters.case_type} onChange={(e) => setFilters((f) => ({ ...f, case_type: e.target.value }))}>
          <option value="">All case types</option>
          {Object.entries(CASE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input style={s.select} placeholder="Status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} />
        <select style={s.select} value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}>
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select style={s.select} value={filters.color} onChange={(e) => setFilters((f) => ({ ...f, color: e.target.value }))}>
          <option value="">All colors</option>
          {Object.entries(COLOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select style={s.select} value={filters.case_control_status} onChange={(e) => setFilters((f) => ({ ...f, case_control_status: e.target.value }))}>
          <option value="">All (Open/Awaiting/Accepted/Done)</option>
          {Object.entries(CASE_CONTROL_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select style={s.select} value={filters.staff_admin_id} onChange={(e) => setFilters((f) => ({ ...f, staff_admin_id: e.target.value }))}>
          <option value="">All staff members</option>
          {staffRoster.map((st) => <option key={st.id} value={st.id}>{st.full_name || st.email}</option>)}
        </select>
        <input style={s.select} type="date" title="From" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
        <input style={s.select} type="date" title="To" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
      </div>

      {selected.size > 0 && (
        <div style={s.bulkBar}>
          {selected.size} selected
          <button style={s.btnGreen} onClick={openBulkAssign}>Assign staff member</button>
          <button style={s.btnGray} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}></th><th style={s.th}></th><th style={s.th}>Country</th><th style={s.th}>Case</th><th style={s.th}>Type</th>
            <th style={s.th}>Status</th><th style={s.th}>Details</th><th style={s.th}>Priority</th><th style={s.th}>Assignment</th><th style={s.th}>Actions</th>
          </tr></thead>
          <tbody>
            {cases.map((c) => (
              <tr key={keyOf(c)}>
                <td style={s.td}><input type="checkbox" checked={selected.has(keyOf(c))} onChange={() => toggleSelect(c)} /></td>
                <td style={s.td} title={COLOR_LABELS[c.color] || c.color}><span style={{ ...s.colorDot, background: COLOR_DOTS[c.color] || '#d1d5db' }} /></td>
                <td style={s.td}>{c.country_code || <button style={s.linkBtn} onClick={() => openCountryModal(c)}>Set country</button>}</td>
                <td style={s.td}>{c.display_id} <span style={s.titleSmall}>{c.title}</span></td>
                <td style={s.td}>{CASE_TYPE_LABELS[c.case_type]}</td>
                <td style={s.td}><span style={s.badge}>{CASE_CONTROL_STATUS_LABELS[c.case_control_status] || c.case_control_status}</span>{c.escalated_at && <span style={s.escalatedTag}>escalated</span>}</td>
                <td style={s.td}><span style={s.titleSmall}>{c.status}</span></td>
                <td style={s.td}>
                  <select style={s.prioritySelect} value={c.priority} onChange={(e) => doPriority(c, e.target.value)}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td style={s.td}>
                  {c.assignment ? (
                    <>
                      <span style={s.badge}>{ASSIGNMENT_LABELS[c.assignment.status] || c.assignment.status}</span>
                      <span style={s.titleSmall}>{staffRoster.find((st) => st.id === c.assignment.assigned_to_admin_id)?.full_name || c.assignment.assigned_to_admin_id?.slice(0, 8)}</span>
                    </>
                  ) : <span style={s.muted}>Unassigned</span>}
                </td>
                <td style={s.td}>
                  {c.case_type === 'support_ticket' && <Link style={{ ...s.btnGray, textDecoration: 'none', display: 'inline-block' }} to={`/case-control/support/${c.case_id}`}>Open case</Link>}
                  <button style={s.btnGray} onClick={() => openAssignSingle(c)}>Assign</button>
                  <button style={s.btnGray} onClick={() => doEscalate(c)}>Escalate</button>
                  <button style={s.btnGray} onClick={() => doDeadline(c)}>Deadline</button>
                  <button style={s.btnGray} onClick={() => doTakeOver(c)}>Take over</button>
                  <button style={s.btnGray} onClick={() => openComments(c)}>Comments</button>
                  {c.assignment && <button style={s.btnGray} onClick={() => openRelease(c)}>Release access</button>}
                  {c.assignment && ['pending_acceptance', 'accepted'].includes(c.assignment.status) && (
                    <button style={s.btnRed} onClick={() => doWithdraw(c)}>Withdraw</button>
                  )}
                  {c.assignment?.status === 'reassignment_requested' && (
                    <>
                      <button style={s.btnGreen} onClick={() => doConfirmReturn(c, true)}>Confirm return</button>
                      <button style={s.btnGray} onClick={() => doConfirmReturn(c, false)}>Require continued work</button>
                      <button style={s.btnGray} onClick={() => openRedistribute(c)}>Redistribute</button>
                    </>
                  )}
                  {c.assignment?.status === 'submitted_for_acceptance' && (
                    <button style={s.btnGreen} onClick={() => openDecision(c)}>Review submission</button>
                  )}
                  {c.assignment?.order_document_id && (
                    <button style={s.btnGray} onClick={() => downloadDocument(c.assignment.order_document_id)}>Order Document · PDF</button>
                  )}
                </td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td style={s.td} colSpan={10}>No cases match these filters.</td></tr>}
          </tbody>
        </table>
      )}

      {withoutCountry.length > 0 && (
        <div style={s.noCountryNote}>{withoutCountry.length} case(s) above have no country assigned — use "Set country" to review them (Owner only).</div>
      )}

      <h2 style={s.h2}>Deadline extension requests</h2>
      <p style={s.hint}>Until you decide, the task's existing deadline still applies.</p>
      <table style={s.table}>
        <thead><tr><th style={s.th}>Case</th><th style={s.th}>Requested by</th><th style={s.th}>Extra time</th><th style={s.th}>Reason</th><th style={s.th}></th></tr></thead>
        <tbody>
          {extensions.map((ex) => (
            <tr key={ex.id}>
              <td style={s.td}>{CASE_TYPE_LABELS[ex.case_type]} <span style={s.titleSmall}>{ex.case_id.slice(0, 8)}</span></td>
              <td style={s.td}>{staffRoster.find((st) => st.id === ex.requested_by_admin_id)?.full_name || ex.requested_by_admin_id?.slice(0, 8)}</td>
              <td style={s.td}>{ex.requested_minutes} min</td>
              <td style={s.td}>{ex.reason}</td>
              <td style={s.td}>
                <button style={s.btnGreen} onClick={() => decideExtension(ex.id, true)}>Approve</button>
                <button style={s.btnGray} onClick={() => decideExtension(ex.id, false)}>Decline</button>
              </td>
            </tr>
          ))}
          {extensions.length === 0 && <tr><td style={s.td} colSpan={5}>No pending extension requests.</td></tr>}
        </tbody>
      </table>

      <h2 style={s.h2}>Emergency access</h2>
      <p style={s.hint}>Time-limited, one case only, selected actions only — expires automatically.</p>
      <button style={s.scanBtn} onClick={() => setShowEmergencyForm((v) => !v)}>{showEmergencyForm ? 'Cancel' : '+ Grant emergency access'}</button>

      {showEmergencyForm && (
        <form onSubmit={submitEmergencyGrant} style={s.form}>
          <select style={s.input} value={emergencyForm.case_type} onChange={(e) => setEmergencyForm((f) => ({ ...f, case_type: e.target.value }))}>
            {Object.entries(CASE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input style={s.input} placeholder="Case ID" value={emergencyForm.case_id} onChange={(e) => setEmergencyForm((f) => ({ ...f, case_id: e.target.value }))} required />
          <select style={s.input} value={emergencyForm.staff_admin_id} onChange={(e) => setEmergencyForm((f) => ({ ...f, staff_admin_id: e.target.value }))} required>
            <option value="">Staff member…</option>
            {staffRoster.map((st) => <option key={st.id} value={st.id}>{st.full_name || st.email}</option>)}
          </select>
          <div style={s.checkRow}>
            {ACTION_OPTIONS.map((a) => (
              <label key={a} style={s.checkLabel}>
                <input type="checkbox" checked={emergencyForm.allowed_actions.includes(a)}
                  onChange={() => setEmergencyForm((f) => ({ ...f, allowed_actions: f.allowed_actions.includes(a) ? f.allowed_actions.filter((x) => x !== a) : [...f.allowed_actions, a] }))} />
                {a.replace(/_/g, ' ')}
              </label>
            ))}
          </div>
          <select style={s.input} value={emergencyForm.duration_hours} onChange={(e) => setEmergencyForm((f) => ({ ...f, duration_hours: e.target.value }))}>
            <option value={2}>2 hours</option>
            <option value={24}>24 hours</option>
          </select>
          <input style={s.input} placeholder="Reason (required)" value={emergencyForm.reason} onChange={(e) => setEmergencyForm((f) => ({ ...f, reason: e.target.value }))} required />
          <button type="submit" style={s.saveBtn}>Grant emergency access</button>
        </form>
      )}

      <table style={s.table}>
        <thead><tr><th style={s.th}>Case</th><th style={s.th}>Staff</th><th style={s.th}>Actions</th><th style={s.th}>Expires</th><th style={s.th}>Status</th><th style={s.th}></th></tr></thead>
        <tbody>
          {emergencyGrants.map((g) => (
            <tr key={g.id}>
              <td style={s.td}>{CASE_TYPE_LABELS[g.case_type]} {g.case_id.slice(0, 8)}</td>
              <td style={s.td}>{staffRoster.find((st) => st.id === g.staff_admin_id)?.full_name || g.staff_admin_id.slice(0, 8)}</td>
              <td style={s.td}>{g.allowed_actions.join(', ')}</td>
              <td style={s.td}>{new Date(g.expires_at).toLocaleString()}</td>
              <td style={s.td}><span style={s.badge}>{g.status}</span></td>
              <td style={s.td}>
                {g.status === 'active' && <>
                  <button style={s.btnGreen} onClick={() => doSendEmergencyTask(g.id)}>Send as task (no acceptance)</button>
                  <button style={s.btnRed} onClick={() => doRevoke(g.id)}>Revoke</button>
                </>}
              </td>
            </tr>
          ))}
          {emergencyGrants.length === 0 && <tr><td style={s.td} colSpan={6}>No emergency access grants.</td></tr>}
        </tbody>
      </table>

      {assignModal?.mode === 'single' && (
        <div style={s.modalBackdrop} onClick={() => setAssignModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Assign case</h3>
            <p style={s.hint}>This sends the task for acceptance — it doesn't make them responsible until they accept.</p>
            {assignModal.loading ? <p>Loading candidates…</p> : (
              <>
                {assignModal.error && <div style={s.errorBox}>{assignModal.error}</div>}
                <textarea style={s.textarea} placeholder="Task message (optional)" value={assignModal.message} onChange={(e) => setAssignModal((m) => ({ ...m, message: e.target.value }))} />
                <p style={s.titleSmall}>For the Order Document (optional — sensible defaults are used if left blank):</p>
                <textarea style={s.textarea} placeholder="Scope of work" value={assignModal.scope_note} onChange={(e) => setAssignModal((m) => ({ ...m, scope_note: e.target.value }))} />
                <input style={s.input} placeholder="Compensation / billing basis" value={assignModal.compensation_note} onChange={(e) => setAssignModal((m) => ({ ...m, compensation_note: e.target.value }))} />
                <input style={s.input} placeholder="Acceptance criteria" value={assignModal.acceptance_criteria_note} onChange={(e) => setAssignModal((m) => ({ ...m, acceptance_criteria_note: e.target.value }))} />
                {assignModal.candidates.map((cand) => (
                  <div key={cand.admin_id} style={{ ...s.candidateRow, opacity: cand.assignable ? 1 : 0.5 }}>
                    <div>
                      <strong>{cand.full_name || cand.email}</strong> <span style={s.titleSmall}>({cand.role_type})</span>
                      {!cand.assignable && <div style={s.reasonText}>✗ {cand.reason}</div>}
                    </div>
                    <button style={s.btnGreen} disabled={!cand.assignable} onClick={() => confirmSingleAssign(cand.admin_id)}>Send task</button>
                  </div>
                ))}
              </>
            )}
            <button style={s.btnGray} onClick={() => setAssignModal(null)}>Close</button>
          </div>
        </div>
      )}

      {assignModal?.mode === 'bulk' && (
        <div style={s.modalBackdrop} onClick={() => setAssignModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Assign {selected.size} case(s)</h3>
            <p style={s.hint}>If even one right is missing for one of the selected cases, assignment is blocked for all of them.</p>
            {assignModal.error && <div style={s.errorBox}>{assignModal.error}</div>}
            <select style={s.input} value={bulkStaffId} onChange={(e) => setBulkStaffId(e.target.value)}>
              <option value="">Staff member…</option>
              {staffRoster.map((st) => <option key={st.id} value={st.id}>{st.full_name || st.email}</option>)}
            </select>
            <button style={s.saveBtn} disabled={!bulkStaffId} onClick={confirmBulkAssign}>Confirm assignment</button>
            <button style={s.btnGray} onClick={() => setAssignModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {countryModal && (
        <div style={s.modalBackdrop} onClick={() => setCountryModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Set case country</h3>
            <select style={s.input} value={countryModal.country_code} onChange={(e) => setCountryModal((m) => ({ ...m, country_code: e.target.value }))}>
              <option value="">Select…</option>
              {countries.map((c) => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
              <option value="ARCHIVE">Global system case (no country)</option>
            </select>
            <input style={s.input} placeholder="Reason (required)" value={countryModal.reason} onChange={(e) => setCountryModal((m) => ({ ...m, reason: e.target.value }))} />
            <button style={s.saveBtn} onClick={saveCountry}>Save country</button>
            <button style={s.btnGray} onClick={() => setCountryModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {redistributeModal && (
        <div style={s.modalBackdrop} onClick={() => setRedistributeModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Redistribute</h3>
            <p style={s.hint}>Confirms the return and immediately sends the task to a new staff member.</p>
            <select style={s.input} value={redistributeModal.staffId} onChange={(e) => setRedistributeModal((m) => ({ ...m, staffId: e.target.value }))}>
              <option value="">Staff member…</option>
              {staffRoster.map((st) => <option key={st.id} value={st.id}>{st.full_name || st.email}</option>)}
            </select>
            <button style={s.saveBtn} disabled={!redistributeModal.staffId} onClick={submitRedistribute}>Redistribute</button>
            <button style={s.btnGray} onClick={() => setRedistributeModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {decisionModal && (
        <div style={s.modalBackdrop} onClick={() => setDecisionModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Review submitted work</h3>
            <p style={s.hint}>Completion report:</p>
            <div style={s.reportBox}>{decisionModal.report || '—'}</div>
            <textarea style={s.textarea} placeholder="Remarks (optional)" value={decisionModal.remarks} onChange={(e) => setDecisionModal((m) => ({ ...m, remarks: e.target.value }))} />
            <button style={s.btnGreen} onClick={() => submitDecision('accepted')}>Accepted</button>
            <button style={s.btnGray} onClick={() => submitDecision('rework_required')}>Rework required</button>
            <button style={s.btnGray} onClick={() => setDecisionModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {releaseModal && (
        <div style={s.modalBackdrop} onClick={() => setReleaseModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Release access — {releaseModal.displayId}</h3>
            <p style={s.hint}>Other people get no access to this case or its documents unless explicitly released here, independently per document.</p>
            {releaseModal.error && <div style={s.errorBox}>{releaseModal.error}</div>}
            {releaseModal.grants.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={s.hint}>Already released to:</div>
                {releaseModal.grants.map((g) => (
                  <div key={g.id} style={s.titleSmall}>
                    {g.grantee?.full_name || g.grantee?.email || g.granted_to_admin_id} — view:{g.grants_view ? 'yes' : 'no'}, order doc:{g.grants_order_document ? 'yes' : 'no'}, delivery note:{g.grants_delivery_note ? 'yes' : 'no'}
                  </div>
                ))}
              </div>
            )}
            <select style={s.select} value={releaseModal.adminId} onChange={(e) => setReleaseModal((m) => ({ ...m, adminId: e.target.value }))}>
              <option value="">Select person…</option>
              {staffRoster.map((st) => <option key={st.id} value={st.id}>{st.full_name || st.email}</option>)}
            </select>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={releaseModal.grantsView} onChange={(e) => setReleaseModal((m) => ({ ...m, grantsView: e.target.checked }))} /> View this case
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={releaseModal.grantsOrder} onChange={(e) => setReleaseModal((m) => ({ ...m, grantsOrder: e.target.checked }))} /> Order Document
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={releaseModal.grantsDelivery} onChange={(e) => setReleaseModal((m) => ({ ...m, grantsDelivery: e.target.checked }))} /> Delivery Note
            </label>
            <button style={s.btnGreen} disabled={!releaseModal.adminId || releaseModal.busy} onClick={submitRelease}>{releaseModal.busy ? 'Saving…' : 'Release'}</button>
            <button style={s.btnGray} onClick={() => setReleaseModal(null)}>Close</button>
          </div>
        </div>
      )}

      {commentsPanel && (
        <div style={s.modalBackdrop} onClick={() => setCommentsPanel(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Comments — {commentsPanel.displayId}</h3>
            {commentsPanel.loading ? <p>Loading…</p> : (
              <div style={s.commentList}>
                {commentsPanel.comments.map((c) => (
                  <div key={c.id} style={s.commentRow}>
                    <div style={s.commentMeta}><strong>{c.author?.full_name || c.author?.email || 'Unknown'}</strong> · {new Date(c.created_at).toLocaleString()}</div>
                    <div>{c.content}</div>
                  </div>
                ))}
                {commentsPanel.comments.length === 0 && <p style={s.empty}>No comments yet.</p>}
              </div>
            )}
            <textarea style={s.textarea} placeholder="Add a comment" value={commentsPanel.draft} onChange={(e) => setCommentsPanel((p) => ({ ...p, draft: e.target.value }))} />
            <button style={s.btnGreen} disabled={!commentsPanel.draft.trim()} onClick={submitComment}>Add comment</button>
            <button style={s.btnGray} onClick={() => setCommentsPanel(null)}>Close</button>
          </div>
        </div>
      )}

      <h2 style={s.h2}>Settings</h2>
      <p style={s.hint}>Default time staff members have to accept a task before it is escalated, by priority.</p>
      <table style={s.table}>
        <thead><tr><th style={s.th}>Priority</th><th style={s.th}>Acceptance window (minutes)</th><th style={s.th}></th></tr></thead>
        <tbody>
          {settings.map((st) => (
            <tr key={st.key}>
              <td style={s.td}>{SETTING_LABELS[st.key] || st.key}</td>
              <td style={s.td}>
                <input style={s.smallInput} type="number" min="1" defaultValue={st.value}
                  onBlur={(e) => { if (e.target.value && e.target.value !== st.value) saveSetting(st.key, e.target.value); }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={s.h2}>Commissioning legal entity</h2>
      <p style={s.hint}>Shown on every Order Document. Confirm the real registered name and address for each country before contractors rely on these documents.</p>
      <table style={s.table}>
        <thead><tr><th style={s.th}>Country</th><th style={s.th}>Legal entity name</th><th style={s.th}>Address</th><th style={s.th}></th></tr></thead>
        <tbody>
          {legalEntities.map((le) => (
            <LegalEntityRow key={le.country_code} entity={le} onSave={saveLegalEntity} />
          ))}
          {legalEntities.length === 0 && <tr><td style={s.td} colSpan={4}>No legal entities on file.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

function LegalEntityRow({ entity, onSave }) {
  const [name, setName] = useState(entity.entity_name);
  const [address, setAddress] = useState(entity.address || '');
  return (
    <tr>
      <td style={s.td}>{entity.country_code}</td>
      <td style={s.td}><input style={s.input} value={name} onChange={(e) => setName(e.target.value)} /></td>
      <td style={s.td}><input style={s.input} value={address} onChange={(e) => setAddress(e.target.value)} /></td>
      <td style={s.td}><button style={s.btnGreen} onClick={() => onSave(entity.country_code, name, address)}>Save</button></td>
    </tr>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1300 },
  breadcrumb: { fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  h2: { fontSize: 18, fontWeight: 800, marginTop: 32, marginBottom: 4 },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 14, maxWidth: 700 },
  filters: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 },
  bulkBar: { display: 'flex', gap: 10, alignItems: 'center', background: '#eff6ff', border: '1px solid #bfdbfe', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13, marginBottom: 10 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  titleSmall: { color: '#9ca3af', fontSize: 11, display: 'block' },
  muted: { color: '#9ca3af', fontSize: 12 },
  escalatedTag: { marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#991b1b', background: '#fee2e2', borderRadius: 999, padding: '2px 6px' },
  colorDot: { display: 'inline-block', width: 12, height: 12, borderRadius: '50%' },
  prioritySelect: { padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700 },
  btnGreen: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnRed: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
  linkBtn: { background: 'none', border: 'none', color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 },
  noCountryNote: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 10, borderRadius: 8, fontSize: 12.5, marginBottom: 14 },
  scanBtn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 12 },
  form: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 420 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 },
  smallInput: { padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, width: 90 },
  textarea: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minHeight: 50, resize: 'vertical' },
  checkRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' },
  saveBtn: { padding: '9px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 },
  modal: { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  candidateRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  reasonText: { color: '#dc2626', fontSize: 11.5 },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 8, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' },
  globalCountryTag: { fontSize: 12.5, fontWeight: 700, color: '#374151', background: '#f3f4f6', borderRadius: 8, padding: '8px 10px' },
  empty: { color: '#9ca3af', fontSize: 13 },
  commentList: { maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  commentRow: { background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 8, fontSize: 13 },
  reportBox: { background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 8, whiteSpace: 'pre-wrap' },
  commentMeta: { fontSize: 11.5, color: '#9ca3af', marginBottom: 2 },
};

export default CaseControl;
